import React from 'react';
import TextField, { OutlinedTextFieldProps } from '@material-ui/core/TextField';
import { string } from 'yup';

const EMAIL_VALIDATOR = string().email();
const REQUIRED = 'Email is required';

export type OnChangeArgs = { name: string; value: string };
export type OnChangeFn = (onChangeArgs: OnChangeArgs) => void;

type EmailInputProps = Omit<OutlinedTextFieldProps, 'onChange' | 'onError' | 'error'> & {
  onChange: OnChangeFn;
  onValidation: (err: string | null) => void;
  required?: boolean;
  validateOnBlur?: boolean; // defaults to true
  validateOnChange?: boolean; // defaults to false
  revalidateOnChange?: boolean; // defaults to true
  error?: string | null;
};
type ValidatorFnResponse = {
  err: boolean;
  value: string;
};

const sanitize = (value = ''): { raw: string; list: string[] } => {
  const result = value.replace(/\s/g, '') ?? '';

  return { raw: result, list: result.split(',').filter(x => x) };
};
const validatorFn = async (value: string): Promise<ValidatorFnResponse> => {
  try {
    const valid = await EMAIL_VALIDATOR.isValid(value);

    return { err: !valid, value };
  } catch (err) {
    return { err: true, value };
  }
};
const buildErrorMessage = (values: string[]): string => {
  return `${values.join(',')} must be a valid email`;
};
const validateValues = async (values: string[]): Promise<string | null> => {
  if (!values.length) return null;

  const validations = await Promise.all(values.map(validatorFn));
  const reducedValidations = validations.reduce((acc: string[], x: ValidatorFnResponse) => {
    if (x.err) {
      acc.push(x.value);
    }
    return acc;
  }, []);

  return reducedValidations.length ? buildErrorMessage(reducedValidations) : null;
};

const EmailInput: React.FC<EmailInputProps> = ({
  size = 'small',
  variant = 'outlined',
  name = 'emailInput',
  validateOnChange = false,
  revalidateOnChange = true,
  validateOnBlur = true,
  required = false,
  error,
  onValidation,
  ...props
}) => {
  const handleValidate = async (values: string[]): Promise<void> => {
    try {
      if (!values?.length && required) {
        onValidation(REQUIRED);
      }

      const validationError = await validateValues(values);

      onValidation(validationError);
    } catch (err) {
      onValidation(null);
    }
  };

  const handleOnBlur = async (e: any): Promise<void> => {
    if (!validateOnBlur) return;

    const { value } = e.target;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { raw: sanitizedRawValue, list: sanitizedListValue } = sanitize(value);
    await handleValidate(sanitizedListValue);
  };

  const handleOnChange = async (e: any): Promise<void> => {
    const { name, value } = e.target;
    const { raw: sanitizedRawValue, list: sanitizedListValue } = sanitize(value);

    // call change handler before maybe validating
    props.onChange({ name, value: sanitizedRawValue });

    if (validateOnChange) {
      await handleValidate(sanitizedListValue);
    }

    // remove error if corrected
    if (error && revalidateOnChange) {
      await handleValidate(sanitizedListValue);
    }
  };

  return (
    <TextField
      type="email"
      fullWidth
      autoComplete="off"
      variant={variant}
      size={size}
      {...props}
      onChange={handleOnChange}
      onBlur={handleOnBlur}
      name={name}
      error={!!error}
      helperText={error}
    />
  );
};

export default EmailInput;
